import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { ToastContext, type ToastMessage, type ToastTone } from './toastContext'
import styles from './Toast.module.css'

const VISIBLE_MS = 2600

/**
 * One toast at a time, bottom of the shell. A new one replaces the old rather than
 * stacking: at 20 scans/min a stack would cover the status card, which is the one thing
 * the operator needs to see.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastMessage | null>(null)
  const timer = useRef<number | undefined>(undefined)
  const nextId = useRef(0)

  const show = useCallback((text: string, tone: ToastTone = 'ok') => {
    nextId.current += 1
    setToast({ id: nextId.current, text, tone })
  }, [])

  useEffect(() => {
    if (toast === null) {
      return
    }

    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setToast(null), VISIBLE_MS)

    return () => window.clearTimeout(timer.current)
  }, [toast])

  const api = useMemo(() => ({ show }), [show])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toast !== null && (
        <div className={`${styles.toast} ${styles[toast.tone]}`} role="status" aria-live="polite">
          {toast.text}
        </div>
      )}
    </ToastContext.Provider>
  )
}
